"use client"

import { useEffect, useState } from "react"
import { usePermissions } from "@/hooks/use-permissions"
import { AI_PERMISSIONS } from "@/lib/auth/permissions.constants"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Loader2, Cpu, ChevronDown } from "lucide-react"
import { toast } from "sonner"
import { getProviderDocLinks } from "./provider-doc-links"
import { ParamKeys } from "./param-keys"
import { useAgentModelOverrideQuery } from "@/hooks/queries/ai-agent-model-config"
import {
  useSaveAgentModelOverrideMutation,
  useResetAgentModelOverrideMutation,
} from "@/hooks/mutations/ai-agent-model-config"
import type { AgentOverrideScopeType } from "@/lib/ai/use-cases"
import styles from "./agent-model-override.module.css"

// Radix Select rejects an empty-string value; this sentinel means "no override".
const USE_DEFAULT = "__default__"

interface AgentModelOverrideProps {
  scopeType: AgentOverrideScopeType
  scopeId: string
}

/**
 * Per-agent AI model override. Lets a permitted user re-point one text agent
 * (email / message) to a non-default chat model + params, persisted to the
 * scoped tenant_ai_configs row. Renders only for users who can manage it.
 * Reset reverts to the use-case default (keeps the agent's system prompt).
 */
export function AgentModelOverride({ scopeType, scopeId }: AgentModelOverrideProps) {
  const { can } = usePermissions()
  const canManage = can(AI_PERMISSIONS.AGENT_MODEL_OVERRIDE)

  const query = useAgentModelOverrideQuery(scopeType, scopeId, canManage)
  const saveMutation = useSaveAgentModelOverrideMutation()
  const resetMutation = useResetAgentModelOverrideMutation()

  const [modelId, setModelId] = useState<string>(USE_DEFAULT)
  const [configText, setConfigText] = useState<string>("")
  // Fallback is optional; USE_DEFAULT here means "inherit the use-case fallback".
  const [fallbackId, setFallbackId] = useState<string>(USE_DEFAULT)
  const [fallbackConfigText, setFallbackConfigText] = useState<string>("")
  const [confirmSameProvider, setConfirmSameProvider] = useState(false)
  // Collapsed by default — most agents run the shared use-case model and never touch
  // this. The header still says whether an override is active, so folding it away
  // cannot hide the one fact that matters.
  const [open, setOpen] = useState(false)

  const data = query.data

  // Sync local editor to the persisted override whenever the query refreshes.
  useEffect(() => {
    if (!data) return
    if (data.override) {
      setModelId(data.override.primary_model_id)
      setConfigText(JSON.stringify(data.override.model_config ?? {}, null, 2))
      setFallbackId(data.override.fallback_model_id ?? USE_DEFAULT)
      setFallbackConfigText(
        data.override.fallback_model_id
          ? JSON.stringify(data.override.fallback_model_config ?? {}, null, 2)
          : "",
      )
    } else {
      setModelId(USE_DEFAULT)
      setConfigText("")
      setFallbackId(USE_DEFAULT)
      setFallbackConfigText("")
    }
  }, [data])

  if (!canManage) return null

  const selectedModel = data?.available_models.find((m) => m.id === modelId)
  const selectedFallback = data?.available_models.find((m) => m.id === fallbackId)
  const docLinks = getProviderDocLinks(selectedModel?.provider_name)
  const configPlaceholder =
    selectedModel?.provider_name === "google"
      ? '{"temperature": 0.3, "maxOutputTokens": 1024}'
      : '{"temperature": 0.3, "maxTokens": 1024}'

  const handleModelChange = (id: string) => {
    setModelId(id)
    // A fallback that now equals the new primary would only fail server-side —
    // drop it back to inherit here.
    if (id !== USE_DEFAULT && id === fallbackId) {
      setFallbackId(USE_DEFAULT)
      setFallbackConfigText("")
    }
    if (id === USE_DEFAULT) {
      setConfigText("")
      return
    }
    // Prefill: keep the saved override's params if re-selecting the same model,
    // otherwise seed with the model's own default config.
    if (data?.override?.primary_model_id === id) {
      setConfigText(JSON.stringify(data.override.model_config ?? {}, null, 2))
      return
    }
    const model = data?.available_models.find((m) => m.id === id)
    setConfigText(
      model && Object.keys(model.config).length > 0 ? JSON.stringify(model.config, null, 2) : "{}",
    )
  }

  const fallbackConfigPlaceholder =
    selectedFallback?.provider_name === "google"
      ? '{"temperature": 0.3, "maxOutputTokens": 1024}'
      : '{"temperature": 0.3, "maxTokens": 1024}'

  const handleFallbackChange = (id: string) => {
    setFallbackId(id)
    if (id === USE_DEFAULT) {
      setFallbackConfigText("")
      return
    }
    if (data?.override?.fallback_model_id === id) {
      setFallbackConfigText(JSON.stringify(data.override.fallback_model_config ?? {}, null, 2))
      return
    }
    const model = data?.available_models.find((m) => m.id === id)
    setFallbackConfigText(
      model && Object.keys(model.config).length > 0 ? JSON.stringify(model.config, null, 2) : "{}",
    )
  }

  // Stop overriding: persists primary_model_id → NULL, model_config → {}.
  const handleSetDefault = async () => {
    setModelId(USE_DEFAULT)
    setConfigText("")
    setFallbackId(USE_DEFAULT)
    setFallbackConfigText("")
    await resetMutation.mutateAsync({ scope_type: scopeType, scope_id: scopeId })
  }

  // Parse a config editor into a params object; toasts + returns null on bad JSON.
  const parseConfig = (text: string, label: string): Record<string, unknown> | null => {
    if (!text.trim()) return {}
    let parsed: unknown
    try {
      parsed = JSON.parse(text)
    } catch {
      toast.error(`Invalid JSON in ${label}`)
      return null
    }
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      toast.error(`${label} must be a JSON object`)
      return null
    }
    return parsed as Record<string, unknown>
  }

  const persist = async () => {
    const parsed = parseConfig(configText, "model configuration")
    if (!parsed) return
    const fallbackParsed =
      fallbackId === USE_DEFAULT ? {} : parseConfig(fallbackConfigText, "fallback configuration")
    if (fallbackParsed === null) return
    await saveMutation.mutateAsync({
      scope_type: scopeType,
      scope_id: scopeId,
      primary_model_id: modelId,
      model_config: parsed,
      fallback_model_id: fallbackId === USE_DEFAULT ? undefined : fallbackId,
      fallback_model_config: fallbackParsed,
    })
  }

  // Commits the current selection — "Use default" means drop the override.
  const handleSave = async () => {
    if (modelId === USE_DEFAULT) {
      await resetMutation.mutateAsync({ scope_type: scopeType, scope_id: scopeId })
      return
    }
    // A fallback on the primary's provider survives a bad model but not a
    // provider outage — the failure this whole feature exists for. Possible,
    // but never by accident.
    if (
      selectedFallback &&
      selectedModel &&
      selectedFallback.provider_name === selectedModel.provider_name
    ) {
      setConfirmSameProvider(true)
      return
    }
    await persist()
  }

  const isBusy = saveMutation.isPending || resetMutation.isPending
  const hasOverride = !!data?.override
  const defaultLabel = data?.effective_default
    ? `Use default (${data.effective_default.display_name})`
    : "Use default"

  // configText is seeded from this exact stringify, so an untouched editor compares equal.
  const persistedModelId = data?.override?.primary_model_id ?? USE_DEFAULT
  const persistedConfigText = data?.override
    ? JSON.stringify(data.override.model_config ?? {}, null, 2)
    : ""
  const persistedFallbackId = data?.override?.fallback_model_id ?? USE_DEFAULT
  const persistedFallbackConfigText = data?.override?.fallback_model_id
    ? JSON.stringify(data.override.fallback_model_config ?? {}, null, 2)
    : ""
  const isDirty =
    modelId !== persistedModelId ||
    configText !== persistedConfigText ||
    fallbackId !== persistedFallbackId ||
    fallbackConfigText !== persistedFallbackConfigText

  const overrideLabel = data?.override
    ? data.available_models.find((m) => m.id === data.override?.primary_model_id)?.display_name ??
      "Custom"
    : null

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer select-none py-4 transition-colors hover:bg-muted/40">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Cpu className="h-4 w-4 text-indigo-600" />
                  <span>AI Model</span>
                  {/* The only signal that survives collapsing: this agent is off-default. */}
                  {overrideLabel && (
                    <Badge variant="secondary" className="font-normal">
                      {overrideLabel}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="mt-1">
                  Override the model this agent uses. Leave on default to inherit the shared
                  use-case model.
                </CardDescription>
              </div>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                  open ? "rotate-180" : ""
                }`}
              />
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-4">
        {query.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading model configuration...</span>
          </div>
        ) : query.isError ? (
          <p className="text-sm text-destructive">Failed to load model configuration.</p>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="agent-model">Model</Label>
              <Select value={modelId} onValueChange={handleModelChange} disabled={isBusy}>
                <SelectTrigger id="agent-model" className={styles.trigger}>
                  {/* Own children — else Radix mirrors the item's two-line markup here
                      and the trigger clamps it to an ellipsis. */}
                  <SelectValue>
                    {modelId === USE_DEFAULT ? (
                      defaultLabel
                    ) : selectedModel ? (
                      <span className={`flex items-baseline gap-2 ${styles.selectedValue}`}>
                        <span className="font-medium">{selectedModel.display_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {selectedModel.provider_display_name} • {selectedModel.model_name}
                        </span>
                      </span>
                    ) : null}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className={styles.content}>
                  <SelectItem value={USE_DEFAULT}>{defaultLabel}</SelectItem>
                  {data?.available_models.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      <div>
                        <div className="font-medium">{model.display_name}</div>
                        <div className="text-xs text-gray-500">
                          {model.provider_display_name} <span>•</span> {model.model_name}
                          <span>{model.cost_label ? ` • ${model.cost_label}` : ''}</span>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {hasOverride
                  ? "This agent overrides the default model."
                  : "This agent uses the default model."}
              </p>
            </div>

            {/* What the agent inherits, as resolved by the runtime — explanatory only. */}
            {modelId === USE_DEFAULT && data?.effective_default && (
              <div className="space-y-2 pt-2 border-t">
                <Label className="text-sm font-medium text-muted-foreground">
                  Default Model Configuration
                </Label>
                <Textarea
                  readOnly
                  rows={5}
                  className="bg-muted/50 font-mono text-sm"
                  value={JSON.stringify(data.effective_default.config ?? {}, null, 2)}
                />
                <p className="text-xs text-muted-foreground">
                  Read-only — the parameters this agent inherits from the shared use-case
                  default. Select a model above to override them.
                </p>
                <p className="text-xs text-muted-foreground pt-1 border-t">
                  <span>Fallback:</span>{" "}
                  <span>{data.effective_default.fallback_display_name
                    ? `inherited (${data.effective_default.fallback_display_name})`
                    : "none configured"}</span>
                  <span>. Override the model above to set a per-agent fallback.</span>
                </p>
              </div>
            )}

            {modelId !== USE_DEFAULT && (
              <div className="space-y-2 pt-2 border-t">
                <Label className="text-sm font-medium text-indigo-700">Model Configuration</Label>
                <Textarea
                  rows={5}
                  className="font-mono text-sm"
                  placeholder={configPlaceholder}
                  value={configText}
                  onChange={(e) => setConfigText(e.target.value)}
                  disabled={isBusy}
                />
                <p className="text-xs text-muted-foreground">
                  <span>JSON parameters — provider&apos;s camelCase keys</span>
                  {docLinks && (
                    <>
                      {" "}
                      (e.g. <ParamKeys keys={docLinks.commonParams} />)
                    </>
                  )}
                  <span>; unknown keys are ignored.</span>
                </p>
              </div>
            )}

            {/* Fallback — only meaningful once the agent overrides its primary. */}
            {modelId !== USE_DEFAULT && (
              <div className="space-y-2 pt-2 border-t">
                <Label htmlFor="agent-fallback">Fallback model</Label>
                <Select value={fallbackId} onValueChange={handleFallbackChange} disabled={isBusy}>
                  <SelectTrigger id="agent-fallback" className={styles.trigger}>
                    <SelectValue>
                      {fallbackId === USE_DEFAULT ? (
                        data?.effective_default?.fallback_display_name
                          ? `Inherit (${data.effective_default.fallback_display_name})`
                          : "Inherit (none configured)"
                      ) : selectedFallback ? (
                        <span className={`flex items-baseline gap-2 ${styles.selectedValue}`}>
                          <span className="font-medium">{selectedFallback.display_name}</span>
                          <span className="text-xs text-muted-foreground">
                            {selectedFallback.provider_display_name} • {selectedFallback.model_name}
                          </span>
                        </span>
                      ) : null}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className={styles.content}>
                    <SelectItem value={USE_DEFAULT}>
                      {data?.effective_default?.fallback_display_name
                        ? `Inherit (${data.effective_default.fallback_display_name})`
                        : "Inherit (none configured)"}
                    </SelectItem>
                    {data?.available_models
                      .filter((m) => m.id !== modelId)
                      .map((model) => (
                        <SelectItem key={model.id} value={model.id}>
                          <div>
                            <div className="font-medium">{model.display_name}</div>
                            <div className="text-xs text-gray-500">
                              {model.provider_display_name} <span>•</span> {model.model_name}
                          <span>{model.cost_label ? ` • ${model.cost_label}` : ''}</span>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Used when the primary is unavailable. Pick a different provider so an outage
                  is actually covered.
                </p>
                {fallbackId !== USE_DEFAULT && (
                  <Textarea
                    rows={4}
                    className="font-mono text-sm"
                    placeholder={fallbackConfigPlaceholder}
                    value={fallbackConfigText}
                    onChange={(e) => setFallbackConfigText(e.target.value)}
                    disabled={isBusy}
                  />
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={handleSetDefault}
                disabled={isBusy || (!hasOverride && modelId === USE_DEFAULT)}
              >
                Set Default
              </Button>
              <Button onClick={handleSave} disabled={isBusy || !isDirty}>
                {saveMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </div>
          </>
        )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>

      <AlertDialog open={confirmSameProvider} onOpenChange={setConfirmSameProvider}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fallback on the same provider?</AlertDialogTitle>
            <AlertDialogDescription>
              <span>The primary and fallback both use</span> {selectedModel?.provider_display_name}<span>. That
              covers a broken model, but not a provider outage — the most common failure. Pick a
              different provider for the fallback, or save anyway.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // AlertDialogAction closes the dialog synchronously; run the save
                // after, not inside, so the mutation isn't torn down mid-flight.
                e.preventDefault()
                setConfirmSameProvider(false)
                void persist()
              }}
            >
              Save anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
